import React, { useContext, useCallback, useEffect } from "react";
import { observer } from "mobx-react";
import { Header } from "src/components/header";
import { RootStoreContext } from "src/stores/RootStore";
import { Route } from "src/config/routes";
import { AppbarButtonProps } from "src/components/header/appbarButtons/AppbarButtons";
import { RouteComponentProps } from "react-router-dom";
import { withRouter } from "react-router";
import { Helmet } from "react-helmet";
import styles from "./Appheader.module.css";
import { UI } from "@wwimmo/ui";
import i18n, { getBrowserLanguage } from "src/utils/i18n";
import { useLanguageDropdownItems } from "src/hooks/translation/useLanguageDropdownItems";
import { NetworkConfig } from "src/network/NetworkConfig";
import { useTranslation } from "react-i18next";
import { Login, COOKIE_PROPERTY_IS_LOGGED_IN, COOKIE_PROPERTY_REFRESH_TOKEN } from "src/screens/auth/login/Login";
import { NOTIFICATION_TYPES } from "src/api/Notification";
import { STEP_TYPES } from "src/api/Processes";

interface MatchParams {
    realestateid: string;
}

const AppheaderBase = (props: RouteComponentProps<MatchParams>) => {
    const { uiStore, navStore, easyContactStore, authStore } = useContext(RootStoreContext);
    const { t } = useTranslation();
    const iconSize = 42;

    const corner = (style: { left?: number; right?: number; transform?: string }) => {
        return (
            <svg
                className={styles.Corner}
                style={style}
                width={`${uiStore.isMobile ? 20 : 40}px`}
                height={`${uiStore.isMobile ? 20 : 40}px`}
                viewBox="0 0 20 20"
            >
                <path
                    d="M0,0 L40,0 L40,40 L0,40 L0,0 Z M20,40 C31.045695,40 40,31.045695 40,20 C40,8.954305 31.045695,0 20,0 C8.954305,0 0,8.954305 0,20 C0,31.045695 8.954305,40 20,40 Z"
                    fill="var(--primary)"
                />
            </svg>
        );
    };

    const CheckIfYarowaOrService7000IsActive = useCallback(async () => {
        let canForwardToThirdPartyUrl = false;
        if (
            easyContactStore.stepType === STEP_TYPES.FINISH &&
            easyContactStore.notification.type === NOTIFICATION_TYPES.DAMAGE_NOTIFICATION
        ) {
            try {
                const response = await fetch(`${NetworkConfig.canForwardToThirdPartyUrl}`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Referer: window.location.origin
                    }
                });

                canForwardToThirdPartyUrl = response.status === 200 && (await response.json()) === 1;
            } catch (fetchError) {
                console.error("Fetch error occured: ", fetchError);
            }
        }

        if (easyContactStore.isLoggedIn !== canForwardToThirdPartyUrl) {
            easyContactStore.setShowProfileAppbarButton(canForwardToThirdPartyUrl);
        }
    }, [easyContactStore]);

    useEffect(() => {
        if (easyContactStore.stepType === STEP_TYPES.FINISH) {
            if (document.cookie !== "") {
                const cookieRows = document.cookie.split("; ");

                const setLoggedIn = cookieRows.some(
                    (cookie) => cookie.startsWith(COOKIE_PROPERTY_IS_LOGGED_IN) && cookie.endsWith("true")
                );

                if (easyContactStore.isLoggedIn !== setLoggedIn) {
                    easyContactStore.setIsLoggedIn(setLoggedIn);
                }

                if (cookieRows.some((cookie) => cookie.startsWith(COOKIE_PROPERTY_REFRESH_TOKEN))) {
                    const refreshTokenCookie = cookieRows.find((cookie) =>
                        cookie.startsWith(COOKIE_PROPERTY_REFRESH_TOKEN)
                    );

                    const refreshToken = refreshTokenCookie?.substring(COOKIE_PROPERTY_REFRESH_TOKEN.length + 1);
                    authStore.setRefreshToken(refreshToken);
                }
            }
        }
    }, [CheckIfYarowaOrService7000IsActive, easyContactStore, easyContactStore.stepType, authStore]);

    const onProfileAppbarButton = useCallback(() => {
        easyContactStore.setShowLogin(true);
    }, [easyContactStore]);

    const onCloseLogin = useCallback(() => {
        easyContactStore.setShowLogin(false);
    }, [easyContactStore]);

    const appbarButtons: AppbarButtonProps[] = [
        {
            svgIcon: UI.SVGIcon.Profile,
            onClick: onProfileAppbarButton,
            isDisplayed: easyContactStore.showProfileAppbarButton
        }
    ];

    const customerName = getComputedStyle(document.documentElement).getPropertyValue("--wwcustomername");

    const { languageDropdownItems } = useLanguageDropdownItems();

    useEffect(() => {
        uiStore.setCurrentLanguageDropdownValue(
            languageDropdownItems.find((languageDropdownItem) => languageDropdownItem.value === getBrowserLanguage())
                ?.label ?? ""
        );
        // eslint-disable-next-line
    }, []);

    const onChangeLanguage = useCallback(
        (language: string) => {
            uiStore.setCurrentLanguageDropdownValue(
                languageDropdownItems.find((languageDropdownItem) => languageDropdownItem.value === language)?.label ??
                    ""
            );

            i18n.changeLanguage(language);
            easyContactStore.setApplicationLanguage(language);
        },
        [languageDropdownItems, easyContactStore, uiStore]
    );

    return (
        <div className={styles.Appheader}>
            {easyContactStore.showLogin ? (
                <>
                    <UI.Modal
                        className={styles.LoginModal}
                        title={easyContactStore.isLoggedIn ? t("labels.ec_manager") : t("screens.auth.login")}
                        show={easyContactStore.showLogin}
                        onClose={onCloseLogin}
                    >
                        {easyContactStore.isLoggedIn ? (
                            <UI.Col>
                                <UI.Button className={"mt-2"} to={Route.logout} label={t("screens.auth.logout")} />
                            </UI.Col>
                        ) : (
                            <Login />
                        )}
                    </UI.Modal>
                </>
            ) : undefined}
            <Helmet>
                <title>{customerName} EasyContact</title>
            </Helmet>
            <UI.Container
                className={styles.Container}
                style={{
                    height: uiStore.headerHeight,
                    paddingTop: 20
                }}
                fluid
            >
                {uiStore.closeAppbar ? (
                    <UI.Row>
                        <UI.Col xs={{ span: 8 }} md={{ span: 3, order: 1 }}>
                            <Header.Backnavigation />
                        </UI.Col>
                        <UI.Col
                            xs={{ span: 12, order: 3 }}
                            sm={{ span: 6, order: 2 }}
                            className="pt-1 text-left text-md-center"
                        >
                            <div className={styles.Titlebar}>
                                <h1>{navStore.appheaderTitle}</h1>
                            </div>
                        </UI.Col>
                        <UI.Col xs={{ span: 4, order: 2 }} md={{ span: 3, order: 3 }} className="text-right">
                            <Header.Close />
                        </UI.Col>
                    </UI.Row>
                ) : (
                    // Show standard Header
                    <UI.Row className="align-items-center">
                        <UI.Col xs={9} sm={8} lg={3}>
                            <Header.Backnavigation />
                        </UI.Col>

                        <UI.Col
                            className="pt-1 text-left text-md-center d-sm-none d-lg-block"
                            xs={{ span: 12, order: 3, offset: 0 }}
                            md={{ span: 6, order: 2, offset: 0 }}
                        >
                            <div className={styles.Titlebar}>
                                <h1
                                    style={
                                        uiStore.isMobile
                                            ? { display: "block", paddingRight: iconSize * 3 }
                                            : { display: "inline", paddingRight: 0 }
                                    }
                                >
                                    <span className="ellipsise">{navStore.appheaderTitle}</span>
                                    <Header.ActionButtons iconSize={iconSize} isMobile={uiStore.isMobile} />
                                </h1>
                            </div>

                            {navStore.currentSubtitle ? <h2>{navStore.currentSubtitle}</h2> : <></>}
                        </UI.Col>
                        <UI.Col
                            className="d-flex flex-nowrap justify-content-end"
                            xs={{ span: 3, order: 2 }}
                            sm={{ span: 4, order: 3, offset: 0 }}
                            lg={{ span: 3, order: 3, offset: 0 }}
                        >
                            <React.Fragment>
                                <Header.AppbarButtons buttons={appbarButtons} />
                                {uiStore.displayChangeLanguageDropdown || easyContactStore.applicationLanguage ? (
                                    <div>
                                        <UI.Dropdown
                                            id="language-dropdown"
                                            className={styles.LanguageDropdown}
                                            label={uiStore.currentLanguageDropdownValue}
                                            items={languageDropdownItems}
                                            onSelect={onChangeLanguage}
                                            iconColor="white"
                                        />
                                    </div>
                                ) : undefined}
                            </React.Fragment>
                        </UI.Col>
                    </UI.Row>
                )}
            </UI.Container>
            <div className={styles.CornerContainer}>
                {corner({ left: 0 })}
                {corner({ right: 0, transform: "scaleX(-1)" })}
            </div>
        </div>
    );
};
export const Appheader = withRouter(observer(AppheaderBase));
